// @ts-check

/**
 * @file Renders a reusable invoice edit select field.
 */

import {useForm} from "@kaspernj/api-maker/build/form"
import PropTypes from "prop-types"
import propTypesExact from "prop-types-exact"
import {memo, useEffect} from "react"
import {shapeComponent, ShapeComponent} from "set-state-compare/build/shape-component.js"
import {View} from "react-native"
import Select from "../../../components/select"
import Text from "@kaspernj/api-maker/build/utils/text"
import useThemeColors, {useThemeName} from "../../../shared/use-theme-colors"

/** @import {NamedExoticComponent, ReactNode} from "react" */
/** @type {Record<string, object>} */
const styles = {}

/** @typedef {{text: string, value: string}} InvoiceEditSelectFieldOption */
/** @typedef {(...args: unknown[]) => unknown} InvoiceEditSelectFieldChangeHandler */
/** @typedef {{[key: string]: unknown, defaultValue?: string, defaultValues?: string[], label: string, multiple?: boolean, name: string, onChangeValue?: InvoiceEditSelectFieldChangeHandler, options: InvoiceEditSelectFieldOption[], search?: boolean, testID: string}} InvoiceEditSelectFieldProps */

/**
 * @typedef {object} InvoiceEditSelectFieldState
 * @property {string[]} selectedValues Current selected option values.
 */

/**
 * Select field used by invoice edit forms.
 * @augments {ShapeComponent<InvoiceEditSelectFieldProps, InvoiceEditSelectFieldState>}
 */
class InvoiceEditSelectField extends ShapeComponent {
  /** @type {ReturnType<typeof useForm>} */
  form
  /** @type {ReturnType<typeof useThemeColors>} */
  colors = this.hookField()
  /** @type {ReturnType<typeof useThemeName>} */
  themeName = this.hookField()
  static propTypes = propTypesExact({
    defaultValue: PropTypes.string,
    defaultValues: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string.isRequired,
    multiple: PropTypes.bool,
    name: PropTypes.string.isRequired,
    onChangeValue: PropTypes.func,
    options: PropTypes.arrayOf(
      PropTypes.shape({
        text: PropTypes.string.isRequired,
        value: PropTypes.string.isRequired
      })
    ).isRequired,
    search: PropTypes.bool,
    testID: PropTypes.string.isRequired
  })
  static defaultProps = {
    defaultValue: undefined,
    defaultValues: undefined,
    multiple: false,
    onChangeValue: undefined,
    search: false
  }
  /** @type {InvoiceEditSelectFieldState} */
  state = {
    selectedValues: /** @type {string[]} */ []
  }

  /** Initializes form value synchronization. */
  setup() {
    const form = useForm()
    this.colors = useThemeColors()
    this.themeName = useThemeName()
    const selectedValues = this.selectedValuesFromProps(this.props)
    const defaultValue = this.props.defaultValue
    const defaultValuesKey = JSON.stringify(this.props.defaultValues || [])
    const multiple = this.props.multiple
    const name = this.props.name
    const optionsKey = JSON.stringify(this.props.options)
    this.form = form
    this.state.selectedValues = selectedValues
    useEffect(() => {
      const nextSelectedValues = this.selectedValuesFromProps(this.props)
      this.s.selectedValues = nextSelectedValues
      this.syncFormValue(nextSelectedValues)
    }, [defaultValue, defaultValuesKey, multiple, name, optionsKey])
  }

  /**
   * Reads selected values from component props.
   * @param {typeof this.props} props Props to read.
   * @returns {string[]} Normalized value list.
   */
  selectedValuesFromProps(props) {
    if (props.multiple) {
      if (Array.isArray(props.defaultValues)) {
        return props.defaultValues.filter(
          (value) => typeof value === "string" && value.length > 0
        )
      }
      if (
        typeof props.defaultValue === "string" &&
        props.defaultValue.length > 0
      ) {
        return [props.defaultValue]
      }
      return []
    }
    if (
      typeof props.defaultValue === "string" &&
      props.defaultValue.length > 0
    ) {
      return [props.defaultValue]
    }
    const firstOption = props.options[0]
    return firstOption ? [firstOption.value] : []
  }

  /**
   * Syncs selected values into the api-maker form.
   * @param {string[]} selectedValues Values to sync.
   * @returns {void} Nothing.
   */
  syncFormValue(selectedValues) {
    const {form} = this.tt
    const {multiple, name} = this.props
    if (!form) return
    form.setValue(name, multiple ? selectedValues : selectedValues[0] || "")
  }

  /**
   * Applies a select value change.
   * @param {(string | number)[] | string | number | undefined} value Selected value.
   * @returns {void} Nothing.
   */
  onChangeValue = (value) => {
    const selectedValues = Array.isArray(value)
      ? value.map(String).filter((entry) => entry.length > 0)
      : typeof value === "string" && value.length > 0
        ? [value]
        : typeof value === "number"
          ? [String(value)]
          : []
    const nextValue = this.props.multiple
      ? selectedValues
      : selectedValues[0] || ""
    this.s.selectedValues = selectedValues
    this.syncFormValue(selectedValues)
    if (this.props.onChangeValue) {
      this.props.onChangeValue(nextValue)
    }
  }

  /**
   * Renders the edit select field.
   * @returns {ReactNode} Select field content.
   */
  render() {
    const {label, multiple, options, search, testID} = this.p
    const {selectedValues} = this.s
    const themeName = this.tt.themeName
    const colors = this.tt.colors
    return (
      <View
        style={styles.field ||= {
          width: "100%"
        }}
        testID={testID}
      >
        <Text
          style={styles[`label-${themeName}`] ||= {
            color: colors.textMuted,
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 6
          }}
          testID={`${testID}/label`}
        >
          {label}
        </Text>
        <Select
          multiple={multiple}
          onChangeValue={this.tt.onChangeValue}
          options={options}
          search={search}
          testID={`${testID}/select`}
          values={selectedValues}
        />
      </View>
    )
  }
}

/** @type {NamedExoticComponent<InvoiceEditSelectFieldProps>} */
const InvoiceEditSelectFieldComponent = memo(shapeComponent(InvoiceEditSelectField))

export default InvoiceEditSelectFieldComponent
